import React, { useCallback, useMemo } from 'react';

import {
  fetchWithToken,
  getEnvValue,
  ENV_CONFIG_KEYS,
} from '@adsk/acs-sc-web-platform-api';
import { useCurrentProjectId } from '@adsk/alloy-provider';
import A from '@adsk/alloy-react-a';
import { useLocale } from '@adsk/alloy-react-locale';
import {
  NOTIFICATION_STATUSES,
  useNotifications,
} from '@adsk/alloy-react-notification';

import { useRelationshipsContext } from '../providers/RelationshipsProvider';

const chunk = (arr, size) => {
  const output = [];
  for (let i = 0; i < arr.length; i += size) {
    output.push(arr.slice(i, i + size));
  }
  return output;
};

export const RELATIONSHIP_ADD_PAGE_SIZE = 20;

/**
 * This hook returns helpers and callbacks to create and delete relationships via the relationship service 3LO API.
 * It must be used inside `RelationshipsProvider` component.
 *
 * Returned value:
 * is3LOSupported - callback to check if a reference type supports 3LO reference creation/deletion. Expects two arguments - a reference entity domain and type.
 * add3LORelationships - callback to add new relationships via 3LO API. Expects two arguments - a source entity and an array of entities to link.
 * remove3LORelationshipById - callback to remove relationship via 3LO API. Expects one argument - the relationship ID to remove.
 *
 * @param plugins
 * @returns {{is3LOSupported: boolean, add3LORelationships: Promise<[]>, remove3LORelationshipById: Promise<string[]>}}
 */
export default (targetPlugins = []) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const { showNotification } = useNotifications();
  const { removeRelationshipById, addRelationships } =
    useRelationshipsContext();
  const projectId = useCurrentProjectId();
  const relationshipAddUrl = useMemo(
    () =>
      `${getEnvValue(
        ENV_CONFIG_KEYS.RELATIONSHIP_SERVICE_URL,
      )}/v2/containers/${projectId}/relationships`,
    [projectId],
  );
  const relationshipRemoveUrl = useMemo(
    () =>
      `${getEnvValue(
        ENV_CONFIG_KEYS.RELATIONSHIP_SERVICE_URL,
      )}/v2/containers/${projectId}/relationships:delete`,
    [projectId],
  );

  const is3LOSupported = useCallback(
    ({ domain, type }) =>
      targetPlugins.some(
        ({ metadata, is3LOSupported }) =>
          metadata.domain === domain &&
          metadata.type === type &&
          is3LOSupported,
      ),
    [targetPlugins],
  );

  const add3LORelationships = useCallback(
    async (
      sourceEntity,
      entitiesToLink,
      options = { includeFailedRelationships: false },
    ) => {
      if (!entitiesToLink.length) return [];

      const requestRelationships = entitiesToLink.map((entity) => ({
        entities: [sourceEntity, entity],
      }));
      const requestRelationshipsBatches = chunk(
        requestRelationships,
        RELATIONSHIP_ADD_PAGE_SIZE,
      );

      const addRelationshipsBatch = async (requestRelationshipsBatch) => {
        let batchFetch;
        try {
          batchFetch = await fetchWithToken(relationshipAddUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestRelationshipsBatch),
          });
        } catch (error) {
          return Promise.reject({
            ...error,
            relationships: requestRelationshipsBatch,
          });
        }

        return batchFetch;
      };

      const relationshipBatches = await Promise.allSettled(
        requestRelationshipsBatches.map(addRelationshipsBatch),
      );

      let newRelationships = [];
      let failedRelationships = [];

      relationshipBatches.forEach((response) => {
        const { status, value, reason } = response;

        if (status === 'fulfilled') {
          newRelationships = [...newRelationships, ...value];
        } else {
          failedRelationships = [
            ...failedRelationships,
            ...reason?.relationships,
          ];

          console.error(
            'ReferencePicker - ReferencePickerModalContainer: Error adding relationships',
            reason,
          );
        }
      });

      if (newRelationships.length === entitiesToLink.length) {
        showNotification({
          status: NOTIFICATION_STATUSES.SUCCESS,
          message: T.translate('ADD_REFERENCE_SUCCESS', {
            context: newRelationships.length,
          }),
        });
      } else {
        const errorMessage = (
          <>
            <span>
              {T.translate('ERROR_ADDING_3LO_RELATIONSHIPS_MESSAGE', {
                context: entitiesToLink.length - newRelationships.length,
                total: entitiesToLink.length,
              })}
            </span>
            {'. '}
            {T.translate('IF_PROBLEM_PERSISTS')}
            <A href="https://knowledge.autodesk.com/contact-support">
              {T.translate('CONTACT_SUPPORT')}
            </A>
            .
          </>
        );

        showNotification({
          status: NOTIFICATION_STATUSES.ERROR,
          message: errorMessage,
        });
      }

      addRelationships(newRelationships);

      if (options.includeFailedRelationships) {
        return {
          fulfilled: newRelationships,
          rejected: failedRelationships,
        };
      }

      return newRelationships;
    },
    [addRelationships, relationshipAddUrl, T, showNotification],
  );

  const remove3LORelationshipById = useCallback(
    async (relationshipId) => {
      let res;

      try {
        res = await fetchWithToken(relationshipRemoveUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify([relationshipId]),
        });
        removeRelationshipById(relationshipId);
      } catch (error) {
        const errorMessage = (
          <>
            <span>{T.translate('ERROR_REMOVING_RELATIONSHIPS_MESSAGE')}</span>
            {'. '}
            {T.translate('IF_PROBLEM_PERSISTS')}
            <A href="https://knowledge.autodesk.com/contact-support">
              {T.translate('CONTACT_SUPPORT')}
            </A>
            .
          </>
        );

        showNotification({
          status: NOTIFICATION_STATUSES.ERROR,
          message: errorMessage,
        });

        return Promise.reject(error);
      }

      return res;
    },
    [T, relationshipRemoveUrl, removeRelationshipById, showNotification],
  );

  return { is3LOSupported, add3LORelationships, remove3LORelationshipById };
};
